import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  DialogTitle,
} from '@mui/material';
import {
  GridRowId,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import useWidgetConfigMutations from '@src/hooks/api/mutations/useWidgetConfigMutations';
import JSONEditor from '@src/components/_elements/JSONEditor/JSONEditor';
import { CollectionPayload } from '@src/types/Collection';
import { WidgetConfigBase, WidgetConfigExtended } from '@src/types/Widget';
import widgetConfigSx from '../configs/widgetConfigSx';

interface DialogProps {
  jsonData: CollectionPayload;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
  handleCancel: (id?: GridRowId) => () => void;
  setJsonPayload: Dispatch<SetStateAction<CollectionPayload | null>>;
}

// eslint-disable-next-line max-lines-per-function
function WidgetConfigDialog({
  jsonData,
  handleCancel,
  setOpen,
  setRowModesModel,
  setJsonPayload,
}: DialogProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { id, deleted, sections, remove, isNew, ...rest } =
    jsonData as WidgetConfigExtended;
  const [newData, setNewData] = useState(rest as WidgetConfigBase);
  const queryClient = useQueryClient();

  const getTitle = () => {
    if (remove) return 'Are you sure you want to delete this config?';
    if (id) return 'Edit widget config';

    return 'Add widget config';
  };

  const { createWidgetConfig, updateWidgetConfig, deleteWidgetConfig } =
    useWidgetConfigMutations(id);

  const handleSuccessCallback = () => {
    if (id && !remove) {
      setRowModesModel((prevState) => ({
        ...prevState,
        [id]: { mode: GridRowModes.View },
      }));
    }

    queryClient.invalidateQueries({
      queryKey: [QueryKeys.widgets],
    });
    setJsonPayload(null);
    setOpen(false);
  };

  const handleCreate = () =>
    createWidgetConfig.mutate(newData, {
      onSuccess: handleSuccessCallback,
    });

  const handlePCUpdate = () =>
    updateWidgetConfig.mutate(newData, {
      onSuccess: handleSuccessCallback,
    });

  const handleDelete = () =>
    deleteWidgetConfig.mutate(newData, {
      onSuccess: handleSuccessCallback,
    });

  const handleConfirm = () => {
    if (remove) return handleDelete();
    if (id) return handlePCUpdate();
    return handleCreate();
  };

  return (
    <Dialog
      open
      PaperProps={
        !remove
          ? {
              sx: widgetConfigSx.pcDialogPaper,
            }
          : {}
      }
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      {!remove && (
        <DialogContent>
          <JSONEditor
            json={newData}
            onChange={setNewData}
            options={{ mode: 'code' }}
          />
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleCancel(id)}>Cancel</Button>
        <Button size='small' variant='contained' onClick={handleConfirm}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default WidgetConfigDialog;
